// src/components/panoramica/WhyDuels.js
// "Perché lui e non lui": per ogni titolare, il confronto con la migliore
// alternativa rimasta in panchina nello stesso ruolo. In cima le scelte più
// in bilico (scarto minimo), con il dettaglio per fattore.
import React, { useMemo, useState } from 'react';
import TeamBadge from '../TeamBadge';
import { compareForecasts, mantraTokensOf } from './lineupOptimizer';
import { getPurchaseName, getPurchaseTeam } from '../auction/panoramicaEngine';

const PRO = {
  base: 'rende di più in stagione',
  form: 'è più in forma',
  opponent: 'ha un avversario più morbido',
  venue: 'gioca in casa',
  bonus: 'porta più bonus attesi',
  availability: 'è più sicuro di giocare',
};
const CON = {
  base: 'rende un po’ meno in stagione',
  form: 'è meno in forma',
  opponent: 'ha un avversario più duro',
  venue: 'gioca fuori casa',
  bonus: 'porta meno bonus',
  availability: 'è meno sicuro di giocare',
};

function verdict(name, altName, cmp) {
  // La probabilità di giocare non è un "pro" qui: tra due che si coprono a
  // vicenda decide il rendimento quando giocano (vedi compareForecasts).
  const pros = cmp.diffs.filter((d) => d.key !== 'availability' && d.diff > 0.08).slice(0, 2).map((d) => PRO[d.key]);
  const con = cmp.diffs.find((d) => d.key !== 'availability' && d.diff < -0.15);
  const head = pros.length
    ? `${name} ${pros.join(' e ')}${con ? `, anche se ${CON[con.key]}` : ''}.`
    : `${name} è avanti di poco: scelta quasi indifferente.`;
  const cover = cmp.starterLessLikely
    ? ` È meno sicuro di giocare, ma se resta fuori entra ${altName}: conviene comunque schierarlo.`
    : '';
  return head + cover;
}

function findAlternative(starter, bench, rolesMode) {
  if (starter.swapAltId) {
    const alt = bench.find((b) => b.forecast.playerId === starter.swapAltId);
    if (alt) return alt;
  }
  if (rolesMode === 'mantra' && starter.slotLabel) {
    const opts = starter.slotLabel.split('/');
    return bench.find((b) => opts.some((o) => mantraTokensOf(b).includes(o)));
  }
  return bench.find((b) => b.forecast.role === starter.forecast.role);
}

function Duel({ starter, alt, cmp, onSelect }) {
  const sName = getPurchaseName(starter.purchase);
  const aName = getPurchaseName(alt.purchase);
  const max = Math.max(0.6, ...cmp.diffs.map((d) => Math.abs(d.diff)));
  return (
    <article className="pz-card pz-duel">
      <div className="pz-duel__head">
        <button type="button" className="pz-duel__side is-in" onClick={() => onSelect(starter)}>
          <TeamBadge team={getPurchaseTeam(starter.purchase)} size={24} showTooltip={false} />
          <span><strong>{sName}</strong><em>titolare · {starter.forecast.expFV.toFixed(1)}</em></span>
        </button>
        <span className="pz-duel__vs">vs</span>
        <button type="button" className="pz-duel__side is-out" onClick={() => onSelect(alt)}>
          <span><strong>{aName}</strong><em>panchina · {alt.forecast.expFV.toFixed(1)}</em></span>
          <TeamBadge team={getPurchaseTeam(alt.purchase)} size={24} showTooltip={false} />
        </button>
      </div>
      <p className="pz-duel__verdict">{verdict(sName, aName, cmp)}</p>
      <ul className="pz-duel__bars">
        {cmp.diffs.filter((d) => Math.abs(d.diff) >= 0.03).map((d) => {
          const w = Math.min(50, (Math.abs(d.diff) / max) * 50);
          return (
            <li key={d.key}>
              <span className="pz-duel__label">{d.key === 'venue' ? 'Campo' : d.label}</span>
              <span className="pz-duel__track">
                <i className={d.diff >= 0 ? 'is-pos' : 'is-neg'} style={d.diff >= 0 ? { left: '50%', width: `${w}%` } : { right: '50%', width: `${w}%` }} />
              </span>
              <span className={`pz-duel__val ${d.diff >= 0 ? 'is-pos' : 'is-neg'}`}>
                {d.key === 'availability' ? `${d.pp >= 0 ? '+' : ''}${Math.round(d.pp * 100)}%` : `${d.diff >= 0 ? '+' : ''}${d.diff.toFixed(1)}`}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="pz-duel__gap">
        Schierare lui invece dell'altro vale <b>{cmp.valueGap >= 0 ? '+' : ''}{cmp.valueGap.toFixed(2)}</b> punti attesi
        {' '}(se gioca: {cmp.expGap >= 0 ? '+' : ''}{cmp.expGap.toFixed(1)} di fantavoto)
      </div>
    </article>
  );
}

export default function WhyDuels({ lineup, onSelect }) {
  const [showAll, setShowAll] = useState(false);
  const duels = useMemo(() => {
    if (!lineup) return [];
    return lineup.starters
      .map((s) => {
        const alt = findAlternative(s, lineup.bench, lineup.rolesMode);
        if (!alt) return null;
        const cmp = compareForecasts(s, alt);
        // Classic: guadagno calcolato dall'ottimizzatore stesso (reparto intero).
        if (Number.isFinite(s.swapGap) && s.swapAltId === alt.forecast.playerId) cmp.valueGap = s.swapGap;
        return { starter: s, alt, cmp };
      })
      .filter(Boolean)
      .sort((a, b) => a.cmp.valueGap - b.cmp.valueGap);
  }, [lineup]);

  if (!duels.length) return <div className="pz-empty">Nessuna alternativa in panchina da confrontare.</div>;
  const shown = showAll ? duels : duels.slice(0, 4);
  return (
    <>
      <div className="pz-duels">
        {shown.map((d) => (
          <Duel key={d.starter.forecast.playerId} {...d} onSelect={onSelect} />
        ))}
      </div>
      {duels.length > 4 && (
        <button type="button" className="pz-more" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Mostra solo le scelte più in bilico' : `Mostra tutti i confronti (${duels.length})`}
        </button>
      )}
    </>
  );
}
